import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import RelocationAffiliate from './RelocationAffiliate';
import RelocationCTA from './RelocationCTA';
import { RelocationBreadcrumb, RelocationSiblingLinks } from './RelocationNav';

const CityPageTemplate = ({ city }) => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": city.faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const images = city.images || {};

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <SEO title={city.seoTitle} description={city.meta} path={city.path} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Header currentPage="relocation" />

      {/* Hero with Image */}
      <section className="relative pt-[100px] lg:pt-[120px]">
        <div className="h-[70vh] lg:h-screen lg:max-h-none relative overflow-hidden">
          {images.hero && (
            <img 
              src={images.hero} 
              alt={`${city.name} hero`} 
              className="w-full h-full object-cover object-center block"
              data-testid="city-hero-image"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/80 via-[#FFFFFF]/40 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 pb-8 lg:pb-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <RelocationBreadcrumb cityName={city.name} />
            <h1 className="text-3xl lg:text-5xl text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="city-hero-title">
              {city.h1}
            </h1>
            <p className="text-base lg:text-lg text-white/80 max-w-2xl leading-relaxed mb-8">
              {city.subheadline}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/relocation/blueprint"
                className="inline-flex items-center gap-3 bg-[#E8A830] text-white px-8 py-4 text-sm font-semibold rounded-lg hover:bg-[#a8511f] transition-all group"
                data-testid="hero-cta-book"
              >
                Get Your Relocation Plan
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/relocation"
                className="inline-flex items-center gap-2 text-sm font-medium text-white border border-white/30 px-6 py-4 rounded-lg hover:bg-white hover:text-[#FFFFFF] transition-all"
                data-testid="hero-cta-hub"
              >
                View All Cities
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[680px] mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl text-[#FFFFFF] mb-10" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="why-heading">
            Why {city.name}?
          </h2>
          <div className="space-y-6 text-[#666] text-lg leading-relaxed">
            {city.whyParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      {images.gallery && images.gallery.length > 0 && (
        <section className="py-12 bg-white border-t border-b border-[#e0dbd4]">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="grid md:grid-cols-3 gap-4">
              {images.gallery.map((img, idx) => (
                <div key={idx} className="relative h-64 overflow-hidden rounded-lg">
                  <img src={img.url} alt={img.alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <p className="text-white text-sm font-medium">{img.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Neighborhoods */}
      <section className="py-20 lg:py-24 bg-white border-t border-[#e0dbd4]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl text-[#FFFFFF] mb-12" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="neighborhoods-heading">
            Best Neighborhoods in {city.name}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {city.neighborhoods.map((n) => (
              <div key={n.name} className="p-6 border border-[#e0dbd4] bg-[#FAF7F2] rounded-lg" data-testid={`neighborhood-${n.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <h3 className="text-[#FFFFFF] font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{n.name}</h3>
                <p className="text-[#666] text-sm mb-3">{n.description}</p>
                <p className="text-[#E8A830] text-xs font-semibold">{n.rent}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost of Living */}
      <section className="py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl text-[#FFFFFF] mb-12" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="cost-heading">
            Cost of Living in {city.name}
          </h2>
          <div className="space-y-4 mb-10">
            {city.costs.map((c) => (
              <div key={c.label} className="flex justify-between items-center py-3 border-b border-[#e0dbd4]">
                <span className="text-[#666] text-sm">{c.label}</span>
                <span className="text-[#FFFFFF] text-sm font-medium">{c.value}</span>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="p-6 border border-[#E8A830]/30 bg-[#E8A830]/5 rounded-lg">
              <p className="text-[#E8A830] text-xs font-semibold mb-1">Single Person Monthly</p>
              <p className="text-[#FFFFFF] text-xl font-light">{city.budgetSingle}</p>
            </div>
            <div className="p-6 border border-[#E8A830]/30 bg-[#E8A830]/5 rounded-lg">
              <p className="text-[#E8A830] text-xs font-semibold mb-1">Couple Monthly</p>
              <p className="text-[#FFFFFF] text-xl font-light">{city.budgetCouple}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate Resources */}
      <RelocationAffiliate />

      {/* Consultation CTA */}
      <RelocationCTA />

      {/* FAQ */}
      <section className="py-20 lg:py-24 bg-white border-t border-[#e0dbd4]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl text-[#FFFFFF] mb-12" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="faq-heading">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            {city.faqs.map((f, i) => (
              <div key={i} data-testid={`faq-${i}`}>
                <h3 className="text-[#FFFFFF] font-semibold mb-3">{f.q}</h3>
                <p className="text-[#666] text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* iter 150 - Reciprocal link to the vetted professional directory.
          Only rendered when the city data carries a `directorySlug`. */}
      {city.directorySlug && (
        <section
          className="py-12 lg:py-16 bg-white border-t border-[#e0dbd4]"
          data-testid="city-guide-directory-block"
        >
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-6 p-6 md:p-7 bg-[#F9F6EF] border border-[#e0dbd4] rounded-2xl">
              <div className="flex-1">
                <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1.5">
                  Vetted local professionals
                </p>
                <p className="text-[#0A1F2E] text-base md:text-lg leading-snug">
                  Need a {city.directoryCity || city.name} immigration attorney or tax advisor?{' '}
                  <span className="text-[#0A1F2E]/70">See our vetted directory.</span>
                </p>
              </div>
              <a
                href={`/find-a-professional/${city.directorySlug}`}
                data-testid="city-guide-directory-cta"
                className="flex-none inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-[#0A1F2E] text-white text-xs font-mono uppercase tracking-[0.14em] hover:bg-[#163448] transition-colors"
              >
                Open the directory →
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Sibling City Links */}
      <RelocationSiblingLinks currentPath={city.path} />

      <Footer />
    </div>
  );
};

export default CityPageTemplate;
